import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { WkoMultiSelect } from "../WkoMultiSelect";
import { notifyError } from "../../utils/notifications";
const MONTHS = [
    { id: 1, name: "January" },
    { id: 2, name: "February" },
    { id: 3, name: "March" },
    { id: 4, name: "April" },
    { id: 5, name: "May" },
    { id: 6, name: "June" },
    { id: 7, name: "July" },
    { id: 8, name: "August" },
    { id: 9, name: "September" },
    { id: 10, name: "October" },
    { id: 11, name: "November" },
    { id: 12, name: "December" },
];
const REGION_ALL_ID = "*";
const parseYearMonth = (value) => {
    if (value == null) {
        return null;
    }
    const match = value.match(/^(\d{4})-(\d{1,2})$/);
    if (!match) {
        return null;
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
        return null;
    }
    return { year, month };
};
const parseCsvStrings = (value) => {
    if (!value) {
        return [];
    }
    return value
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
};
const parseCsvNumbers = (value) => {
    return parseCsvStrings(value)
        .map((item) => Number(item))
        .filter((item) => Number.isInteger(item));
};
const parseOptionalNumber = (value) => {
    if (value == null || value.length === 0) {
        return undefined;
    }
    const parsed = Number(value);
    return Number.isInteger(parsed) ? parsed : undefined;
};
const getDefaultDateRange = () => {
    const now = new Date();
    const then = new Date(now.getFullYear(), now.getMonth() - 3, 1);
    return {
        selectedMonthFrom: then.getMonth() + 1,
        selectedYearFrom: then.getFullYear(),
        selectedMonthTo: now.getMonth() + 1,
        selectedYearTo: now.getFullYear(),
    };
};
export const createInitialStatsFilterState = (search) => {
    const searchParams = new URLSearchParams(search);
    const defaults = getDefaultDateRange();
    const fromMonth = parseYearMonth(searchParams.get("fromMonth"));
    const toMonth = parseYearMonth(searchParams.get("toMonth"));
    return {
        selectedFleetIds: parseCsvNumbers(searchParams.get("fleets")),
        selectedVesselTypes: parseCsvStrings(searchParams.get("vesselTypes")),
        selectedMonthFrom: fromMonth?.month ?? defaults.selectedMonthFrom,
        selectedYearFrom: fromMonth?.year ?? defaults.selectedYearFrom,
        selectedMonthTo: toMonth?.month ?? defaults.selectedMonthTo,
        selectedYearTo: toMonth?.year ?? defaults.selectedYearTo,
        selectedRegion: REGION_ALL_ID,
        selectedLocationId: parseOptionalNumber(searchParams.get("locationId")),
        isIndustryMode: (searchParams.get("isIndustryMode") ?? "true") === "true",
    };
};
export const buildStatsLinkBack = (filterState) => {
    const params = new URLSearchParams({
        fromMonth: `${filterState.selectedYearFrom}-${filterState.selectedMonthFrom}`,
        toMonth: `${filterState.selectedYearTo}-${filterState.selectedMonthTo}`,
        vesselTypes: filterState.selectedVesselTypes.join(","),
        fleets: filterState.selectedFleetIds.join(","),
        locationId: filterState.selectedLocationId == null ? "" : String(filterState.selectedLocationId),
        isIndustryMode: String(filterState.isIndustryMode),
    });
    return `/Benchmarks/IndustryStats?${params.toString()}`;
};
const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year > currentYear - 7; year -= 1) {
        years.push(year);
    }
    return years;
};
const normalizeFleetIds = (selectedFleetIds, fleets) => {
    const fleetIds = fleets.map((fleet) => fleet.ID);
    const validSelection = selectedFleetIds.filter((id) => fleetIds.includes(id));
    return validSelection.length > 0 ? validSelection : fleetIds;
};
const normalizeVesselTypes = (selectedVesselTypes, vesselTypes, userVesselTypes) => {
    const validSelection = selectedVesselTypes.filter((item) => vesselTypes.includes(item));
    if (validSelection.length > 0) {
        return validSelection;
    }
    const validUserTypes = userVesselTypes.filter((item) => vesselTypes.includes(item));
    if (validUserTypes.length > 0) {
        return validUserTypes;
    }
    return vesselTypes;
};
const normalizeRegions = (regions) => [
    { Id: REGION_ALL_ID, Name: "All Regions" },
    ...regions.map((region) => ({ Id: String(region.Id), Name: region.Name })),
];
const fetchJson = async (url) => {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to load ${url}`);
    }
    return response.json();
};
const loadStatsFilterOptions = async () => {
    const [fleets, vesselTypes, userVesselTypes, regions] = await Promise.all([
        fetchJson("/api/lists/fleets"),
        fetchJson("/api/lists/vesseltypes"),
        fetchJson("/api/lists/vesseltypesinuserfleet"),
        fetchJson("/api/locations/regions"),
    ]);
    return {
        fleets,
        vesselTypes,
        userVesselTypes,
        regions: normalizeRegions(regions),
    };
};
const reconcileLoadedFilterState = (current, options) => ({
    ...current,
    selectedFleetIds: normalizeFleetIds(current.selectedFleetIds, options.fleets),
    selectedVesselTypes: normalizeVesselTypes(current.selectedVesselTypes, options.vesselTypes, options.userVesselTypes),
    selectedRegion: options.regions.some((region) => region.Id === current.selectedRegion)
        ? current.selectedRegion
        : (options.regions[0]?.Id ?? REGION_ALL_ID),
});
export const StatsFilter = ({ filterState, onFilterChange, onControlsLoadedChange, showRegionFilter = false, }) => {
    const [fleets, setFleets] = useState([]);
    const [vesselTypes, setVesselTypes] = useState([]);
    const [regions, setRegions] = useState([]);
    const [industryVesselCount, setIndustryVesselCount] = useState("0");
    const yearOptions = useMemo(() => getYearOptions(), []);
    const selectedFleetOptions = useMemo(() => fleets
        .filter((fleet) => filterState.selectedFleetIds.includes(fleet.ID))
        .map((fleet) => ({ value: fleet.ID, label: fleet.Name })), [fleets, filterState.selectedFleetIds]);
    useEffect(() => {
        let disposed = false;
        onControlsLoadedChange(false);
        const loadOptions = async () => {
            try {
                const loadedOptions = await loadStatsFilterOptions();
                if (disposed) {
                    return;
                }
                setFleets(loadedOptions.fleets);
                setVesselTypes(loadedOptions.vesselTypes);
                setRegions(loadedOptions.regions);
                onFilterChange((current) => reconcileLoadedFilterState(current, loadedOptions));
                onControlsLoadedChange(true);
            }
            catch {
                if (!disposed) {
                    notifyError("An error has occurred when loading the stats filters. Please try again.");
                }
            }
        };
        void loadOptions();
        return () => {
            disposed = true;
        };
    }, [onControlsLoadedChange, onFilterChange]);
    useEffect(() => {
        if (filterState.selectedVesselTypes.length === 0) {
            setIndustryVesselCount("0");
            return;
        }
        const timerId = window.setTimeout(() => {
            fetch(`/api/benchmark/industryvesselscount?${new URLSearchParams({
                vesselTypes: filterState.selectedVesselTypes.join(","),
            })}`)
                .then((response) => {
                if (!response.ok) {
                    throw new Error(response.statusText);
                }
                return response.text();
            })
                .then((count) => {
                setIndustryVesselCount(count);
            })
                .catch(() => {
                notifyError("An error has occurred when retrieving Industry Vessel count. Please try again.");
            });
        }, 200);
        return () => {
            window.clearTimeout(timerId);
        };
    }, [filterState.selectedVesselTypes]);
    const updateDateRange = (field, value) => {
        onFilterChange((current) => {
            const next = { ...current, [field]: value };
            if (field === "selectedMonthFrom" &&
                next.selectedYearFrom === next.selectedYearTo &&
                next.selectedMonthFrom > next.selectedMonthTo) {
                next.selectedMonthTo = next.selectedMonthFrom;
            }
            if (field === "selectedYearFrom") {
                if (next.selectedYearFrom > next.selectedYearTo) {
                    next.selectedYearTo = next.selectedYearFrom;
                }
                if (next.selectedYearFrom === next.selectedYearTo && next.selectedMonthFrom > next.selectedMonthTo) {
                    next.selectedMonthTo = next.selectedMonthFrom;
                }
            }
            if (field === "selectedMonthTo" &&
                next.selectedYearFrom === next.selectedYearTo &&
                next.selectedMonthTo < next.selectedMonthFrom) {
                next.selectedMonthFrom = next.selectedMonthTo;
            }
            if (field === "selectedYearTo") {
                if (next.selectedYearTo < next.selectedYearFrom) {
                    next.selectedYearFrom = next.selectedYearTo;
                }
                if (next.selectedYearFrom === next.selectedYearTo && next.selectedMonthTo < next.selectedMonthFrom) {
                    next.selectedMonthFrom = next.selectedMonthTo;
                }
            }
            return next;
        });
    };
    const updateFleetSelection = (fleetIds) => {
        if (fleetIds.length === 0) {
            notifyError("Selected must contain at least 1 item, 1 selected item retained!");
            onFilterChange((current) => ({
                ...current,
                selectedFleetIds: current.selectedFleetIds.slice(0, 1),
            }));
            return;
        }
        onFilterChange((current) => ({
            ...current,
            selectedFleetIds: fleetIds,
        }));
    };
    const toggleVesselType = (vesselType, isChecked) => {
        onFilterChange((current) => {
            const selectedVesselTypes = isChecked
                ? [...current.selectedVesselTypes, vesselType]
                : current.selectedVesselTypes.filter((item) => item !== vesselType);
            if (selectedVesselTypes.length === 0) {
                notifyError("Selected must contain at least 1 item, operation cancelled!");
                return current;
            }
            return {
                ...current,
                selectedVesselTypes,
            };
        });
    };
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "mb-3", children: [_jsxs("div", { className: "form-check", children: [_jsx("input", { autoComplete: "off", checked: filterState.isIndustryMode, className: "form-check-input", id: "displayIndustry", name: "dataDisplayType", onChange: () => onFilterChange((current) => ({ ...current, isIndustryMode: true })), type: "radio" }), _jsx("label", { className: "form-check-label", "data-bs-placement": "top", "data-bs-toggle": "tooltip", htmlFor: "displayIndustry", title: "Display industry benchmark data", children: "Industry benchmark" })] }), _jsxs("div", { className: "form-check", children: [_jsx("input", { autoComplete: "off", checked: !filterState.isIndustryMode, className: "form-check-input", id: "displayLevels", name: "dataDisplayType", onChange: () => onFilterChange((current) => ({ ...current, isIndustryMode: false })), type: "radio" }), _jsx("label", { className: "form-check-label", "data-bs-placement": "top", "data-bs-toggle": "tooltip", htmlFor: "displayLevels", title: "Display analysis by NC level", children: "Levels-based comparison" })] })] }), _jsxs("div", { className: "mb-3", children: [_jsxs("div", { className: "input-group mb-2", children: [_jsx("span", { className: "input-group-text w-25", children: "From" }), _jsx("label", { className: "visually-hidden", htmlFor: "startMonth", children: "From Month" }), _jsx("select", { autoComplete: "off", className: "form-select", id: "startMonth", onChange: (event) => updateDateRange("selectedMonthFrom", Number(event.target.value)), value: filterState.selectedMonthFrom, children: MONTHS.map((month) => (_jsx("option", { value: month.id, children: month.name }, month.id))) }), _jsx("label", { className: "visually-hidden", htmlFor: "startYear", children: "From Year" }), _jsx("select", { autoComplete: "off", className: "form-select", id: "startYear", onChange: (event) => updateDateRange("selectedYearFrom", Number(event.target.value)), value: filterState.selectedYearFrom, children: yearOptions.map((year) => (_jsx("option", { value: year, children: year }, year))) })] }), _jsxs("div", { className: "input-group", children: [_jsx("span", { className: "input-group-text w-25", children: "To" }), _jsx("label", { className: "visually-hidden", htmlFor: "endMonth", children: "To Month" }), _jsx("select", { autoComplete: "off", className: "form-select", id: "endMonth", onChange: (event) => updateDateRange("selectedMonthTo", Number(event.target.value)), value: filterState.selectedMonthTo, children: MONTHS.map((month) => (_jsx("option", { value: month.id, children: month.name }, month.id))) }), _jsx("label", { className: "visually-hidden", htmlFor: "endYear", children: "To Year" }), _jsx("select", { autoComplete: "off", className: "form-select", id: "endYear", onChange: (event) => updateDateRange("selectedYearTo", Number(event.target.value)), value: filterState.selectedYearTo, children: yearOptions.map((year) => (_jsx("option", { value: year, children: year }, year))) })] })] }), _jsx("p", { className: "small mb-0", children: "Your fleet:" }), _jsxs("div", { className: "mb-3", children: [_jsx("label", { className: "visually-hidden", htmlFor: "fleetList", children: "Fleet" }), _jsx("div", { id: "fleetList", children: _jsx(WkoMultiSelect, { options: fleets.map((fleet) => ({ value: fleet.ID, label: fleet.Name })), onChange: (selectedOptions) => updateFleetSelection(selectedOptions.map((option) => option.value)), selectedCountText: (count) => `${count} fleets selected`, value: selectedFleetOptions }) })] }), showRegionFilter ? (_jsxs("div", { className: "mb-3", children: [_jsx("label", { className: "visually-hidden", htmlFor: "regionList", children: "Region" }), _jsx("select", { autoComplete: "off", className: "form-select", id: "regionList", onChange: (event) => onFilterChange((current) => ({ ...current, selectedRegion: event.target.value })), value: filterState.selectedRegion, children: regions.map((region) => (_jsx("option", { value: region.Id, children: region.Name }, region.Id))) })] })) : null, filterState.isIndustryMode ? (_jsxs("div", { className: "row mb-2", children: [_jsxs("div", { className: "col-12", children: [_jsx("p", { className: "small mb-1", children: "Industry:" }), _jsx("div", { className: "row g-2", children: vesselTypes.map((vesselType) => {
                                    const inputId = `cbVesselType${vesselType.replace(/\s/g, "_")}`;
                                    return (_jsx("div", { className: "col-2 col-lg-4", children: _jsxs("div", { className: "form-check", children: [_jsx("input", { autoComplete: "off", checked: filterState.selectedVesselTypes.includes(vesselType), className: "form-check-input privilege-select", id: inputId, name: "vesselTypes", onChange: (event) => toggleVesselType(vesselType, event.target.checked), title: vesselType, type: "checkbox" }), _jsx("label", { className: "form-check-label", htmlFor: inputId, title: vesselType, children: vesselType })] }) }, vesselType));
                                }) })] }), _jsx("div", { className: "col-12", children: _jsxs("p", { className: "small mb-0 text-end", children: ["Comparing against ", _jsx("span", { children: industryVesselCount }), " industry vessels"] }) })] })) : null] }));
};
